import React, { useState, useEffect, useMemo } from 'react';
import { database, ref, get } from '../../firebase';

const UserFundHistory = () => {
  // Data states
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [expandedTransaction, setExpandedTransaction] = useState(null);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Toggle transaction accordion
  const toggleTransactionAccordion = (transactionId) => {
    setExpandedTransaction(expandedTransaction === transactionId ? null : transactionId);
  };

  // Helper function to determine if transaction is a Welcome Bonus
  const isWelcomeBonus = (transaction) => {
    // Check by payment method
    if (transaction.paymentMethod === 'bonus' || 
        transaction.paymentDisplayMethod === 'Registration Welcome Bonus') {
      return true;
    }
    
    // Check by description (only exact welcome bonus match)
    if (transaction.description) {
      const desc = transaction.description.toLowerCase();
      if (desc.includes('welcome bonus') && 
          !desc.includes('user added') && 
          !desc.includes('admin')) {
        return true;
      }
    }
    
    // Check by amount and source combination
    if (transaction.amount === 3 && 
        transaction.source === 'Aerovolt' && 
        transaction.paymentId?.startsWith('BONUS_')) {
      return true;
    }
    
    // Check transaction type field
    if (transaction.type === 'bonus') {
      return true;
    }
    
    return false;
  };

  // Helper function to determine if transaction is User Added (via payment)
  const isUserAdded = (transaction) => {
    if (transaction.type === 'User Added') {
      return true;
    }
    
    if (transaction.paymentId && 
        !transaction.paymentId.startsWith('BONUS_') &&
        transaction.amount !== 3) {
      return true;
    }
    
    if (transaction.currency && 
        transaction.paymentMethod !== 'bonus' &&
        !transaction.description?.toLowerCase().includes('welcome bonus')) {
      return true;
    }
    
    return false;
  };

  // Fetch all transactions from UserFundTransactions using Firebase SDK
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const transactionsRef = ref(database, 'UserFundTransactions');
      const snapshot = await get(transactionsRef);
     
      const transactionsArray = [];
     
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        // Iterate through date folders (2026-02-17 format)
        for (const date in data) {
          const usersByDate = data[date];
          
          // Iterate through users in this date (stored by sponsorId)
          for (const sponsorId in usersByDate) {
            const userTransactions = usersByDate[sponsorId];
            
            // Iterate through transactions for this user
            for (const transactionId in userTransactions) {
              const transaction = userTransactions[transactionId];
              
              if (transaction) {
                // Determine transaction type
                const bonus = isWelcomeBonus(transaction);
                const userAdded = !bonus && isUserAdded(transaction);
                
                // Determine status
                const status = transaction.status || 'completed';
                
                // Determine type display based on actual transaction type
                let displayType = 'Other';
                let typeIcon = '';
                
                if (bonus) {
                  displayType = 'Bonus';
                  typeIcon = '🎁';
                } else if (userAdded || transaction.type === 'User Added') {
                  displayType = 'User Added';
                  typeIcon = '👤';
                } else if (transaction.type === 'admin' || transaction.source === 'admin') {
                  displayType = 'Admin';
                  typeIcon = '👑';
                } else {
                  displayType = transaction.type || 'User Added';
                  typeIcon = '📝';
                }
                
                // Create proper description if missing
                let description = transaction.description || '';
                if (!description) {
                  if (bonus) {
                    description = 'Welcome Bonus for Registration';
                  } else if (userAdded) {
                    description = `User added funds via ${transaction.displayCurrency || transaction.currency || 'Crypto'}`;
                  }
                }
                
                transactionsArray.push({
                  id: transactionId,
                  transactionId: transaction.transactionId || transactionId,
                  sponsorId: transaction.sponsorId || sponsorId || '',
                  memberId: transaction.memberId || '',
                  amount: parseFloat(transaction.amount) || 0,
                  userAddedFund: parseFloat(transaction.userAddedFund) || 0,
                  wallet: transaction.wallet || transaction.paymentMethod || '',
                  type: bonus ? 'bonus' : (userAdded ? 'User Added' : (transaction.type || 'User Added')),
                  displayType: displayType,
                  typeIcon: typeIcon,
                  description: description,
                  timestamp: transaction.timestampFormatted || 
                            (transaction.timestamp ? new Date(transaction.timestamp).toISOString() : new Date().toISOString()),
                  userName: transaction.userName || '',
                  fundBalance: parseFloat(transaction.fundBalance) || 0,
                  previousFundBalance: parseFloat(transaction.previousFundBalance) || 0,
                  userStatus: transaction.userStatus || '',
                  fundCount: transaction.fundCount || 0,
                  lastFundAmount: parseFloat(transaction.lastFundAmount) || 0,
                  lastFundDate: transaction.lastFundDate || null,
                  source: transaction.source || '',
                  network: transaction.network || '',
                  paymentMethod: transaction.paymentMethod || '',
                  paymentDisplayMethod: transaction.paymentDisplayMethod || '',
                  paymentId: transaction.paymentId || '',
                  currency: transaction.currency || '',
                  displayCurrency: transaction.displayCurrency || '',
                  copyCount: transaction.copyCount || 0,
                  scanCount: transaction.scanCount || 0,
                  expiryTime: transaction.expiryTime || null,
                  startTime: transaction.startTime || null,
                  status: status,
                  dateFolder: date,
                  isBonus: bonus,
                  isUserAdded: userAdded
                });
              }
            }
          }
        }
      }
     
      // Sort by timestamp descending (newest first)
      transactionsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      // Add serial numbers
      transactionsArray.forEach((transaction, index) => {
        transaction.sno = index + 1;
      });
      
      setTransactions(transactionsArray);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchTransactions();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Format time
  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid time';
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    if (status === 'completed' || status === 'success') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30">
          <span className="h-1.5 w-1.5 bg-green-400 rounded-full mr-1"></span>
          Completed
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          <span className="h-1.5 w-1.5 bg-yellow-400 rounded-full mr-1 animate-pulse"></span>
          Pending
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
          <span className="h-1.5 w-1.5 bg-red-400 rounded-full mr-1"></span>
          Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
        <span className="h-1.5 w-1.5 bg-blue-400 rounded-full mr-1"></span>
        {status}
      </span>
    );
  };

  // Get transaction type badge
  const getTypeBadge = (transaction) => {
    if (transaction.isBonus || transaction.type === 'bonus') {
      // return (
      //   <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
      //     🎁 Welcome Bonus
      //   </span>
      // );
    }
    if (transaction.isUserAdded || transaction.type === 'User Added') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
          👤 User Added
        </span>
      );
    }
    if (transaction.type === 'admin') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30">
          👑 Admin Added
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400 border border-gray-500/30">
        {transaction.displayType || transaction.type}
      </span>
    );
  };

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions
      .filter(transaction => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm || 
          (transaction.memberId || '').toLowerCase().includes(searchLower) ||
          (transaction.userName || '').toLowerCase().includes(searchLower) ||
          (transaction.sponsorId || '').toLowerCase().includes(searchLower) ||
          (transaction.transactionId || '').toLowerCase().includes(searchLower) ||
          (transaction.paymentId || '').toLowerCase().includes(searchLower) ||
          (transaction.description || '').toLowerCase().includes(searchLower);
        
        const matchesDate = !dateFilter || 
          new Date(transaction.timestamp).toLocaleDateString() === new Date(dateFilter).toLocaleDateString();
        
        const matchesType = !typeFilter || 
          (transaction.type?.toLowerCase().includes(typeFilter.toLowerCase())) ||
          (transaction.displayType?.toLowerCase().includes(typeFilter.toLowerCase())) ||
          (typeFilter === 'bonus' && transaction.isBonus) ||
          (typeFilter === 'User Added' && transaction.isUserAdded);
        
        const matchesPaymentMethod = !paymentMethodFilter || 
          transaction.paymentMethod?.toLowerCase().includes(paymentMethodFilter.toLowerCase()) ||
          transaction.paymentDisplayMethod?.toLowerCase().includes(paymentMethodFilter.toLowerCase()) ||
          transaction.displayCurrency?.toLowerCase().includes(paymentMethodFilter.toLowerCase());
        
        return matchesSearch && matchesDate && matchesType && matchesPaymentMethod;
      });
  }, [transactions, searchTerm, dateFilter, typeFilter, paymentMethodFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalTransactions = filteredTransactions.length;
    const totalAmount = filteredTransactions.reduce((sum, t) => sum + (t.amount || t.userAddedFund || 0), 0);
    const today = new Date().toLocaleDateString();
    const todayTransactions = filteredTransactions.filter(t => 
      new Date(t.timestamp).toLocaleDateString() === today
    ).length;
    const todayAmount = filteredTransactions
      .filter(t => new Date(t.timestamp).toLocaleDateString() === today)
      .reduce((sum, t) => sum + (t.amount || t.userAddedFund || 0), 0);
    
    // Count by type using proper detection
    const userAddedCount = filteredTransactions.filter(t => t.isUserAdded || t.type === 'User Added').length;
    const bonusCount = filteredTransactions.filter(t => t.isBonus || t.type === 'bonus').length;
    const adminCount = filteredTransactions.filter(t => t.type === 'admin').length;
    
    const userAddedAmount = filteredTransactions
      .filter(t => t.isUserAdded || t.type === 'User Added')
      .reduce((sum, t) => sum + (t.amount || t.userAddedFund || 0), 0);
    const bonusAmount = filteredTransactions
      .filter(t => t.isBonus || t.type === 'bonus')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    const adminAmount = filteredTransactions
      .filter(t => t.type === 'admin')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    
    return {
      totalTransactions,
      totalAmount,
      todayTransactions,
      todayAmount,
      userAddedCount,
      bonusCount,
      adminCount,
      userAddedAmount,
      bonusAmount,
      adminAmount
    };
  }, [filteredTransactions]);

  // Handle refresh
  const handleRefresh = () => {
    fetchTransactions();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDateFilter('');
    setTypeFilter('');
    setPaymentMethodFilter('');
    setExpandedTransaction(null);
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'S.No',
      'Date',
      'Time',
      'Type',
      'Sponsor ID',
      'Member ID',
      'Member Name',
      'Amount',
      'Payment Method',
      'Network',
      'Transaction ID',
      'Payment ID',
      'Status',
      'Previous Balance',
      'New Balance',
      'Description',
      'Source'
    ];
    
    const csvData = filteredTransactions.map(t => [
      t.sno,
      formatDate(t.timestamp),
      formatTime(t.timestamp),
      t.displayType || t.type,
      t.sponsorId,
      t.memberId,
      t.userName,
      t.amount || t.userAddedFund || 0,
      t.paymentDisplayMethod || t.paymentMethod || 'Crypto',
      t.network || '-',
      t.transactionId,
      t.paymentId || '-',
      t.status,
      t.previousFundBalance || 0,
      t.fundBalance || 0,
      t.description || '-',
      t.source || '-'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `user_fund_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Mobile Accordion Item Component
  const MobileTransactionCard = ({ transaction }) => (
    <div className="bg-navy-dark border border-border rounded-lg mb-2 overflow-hidden">
      <button
        onClick={() => toggleTransactionAccordion(transaction.id)}
        className="w-full p-3 flex justify-between items-center hover:bg-navy-dark/80 transition-colors"
      >
        <div className="text-left flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-silver-light text-sm font-semibold">
              {transaction.userName || transaction.memberId}
            </span>
            {getTypeBadge(transaction)}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-cyan-accent text-xs">
              {transaction.sponsorId}
            </span>
            {getStatusBadge(transaction.status)}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-blue-400 text-sm font-bold">
              ${(transaction.amount || transaction.userAddedFund || 0).toFixed(2)}
            </div>
            <div className="text-silver text-xs">
              {formatDate(transaction.timestamp)}
            </div>
          </div>
          <svg 
            className={`w-4 h-4 text-silver transition-transform ${expandedTransaction === transaction.id ? 'transform rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      {expandedTransaction === transaction.id && (
        <div className="p-3 bg-navy-dark/70 border-t border-border/30">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-silver text-xs">Member ID:</div>
              <div className="text-silver-light text-sm font-medium">{transaction.memberId}</div>
            </div>
            <div>
              <div className="text-silver text-xs">Sponsor ID:</div>
              <div className="text-silver-light text-sm font-medium">
                {transaction.sponsorId}
              </div>
            </div>
            <div>
              <div className="text-silver text-xs">Transaction ID:</div>
              <div className="text-silver-light text-xs font-mono truncate">{transaction.transactionId}</div>
            </div>
            <div>
              <div className="text-silver text-xs">Payment ID:</div>
              <div className="text-silver-light text-xs font-mono truncate">{transaction.paymentId}</div>
            </div>
            <div>
              <div className="text-silver text-xs">Network:</div>
              <div className="text-silver-light text-sm">{transaction.network}</div>
            </div>
            <div>
              <div className="text-silver text-xs">Source:</div>
              <div className="text-silver-light text-sm">{transaction.source || '-'}</div>
            </div>
            <div>
              <div className="text-silver text-xs">Previous Balance:</div>
              <div className="text-silver-light text-sm">${transaction.previousFundBalance?.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-silver text-xs">New Balance:</div>
              <div className="text-blue-400 text-sm font-bold">${transaction.fundBalance?.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-silver text-xs">Payment Method:</div>
              <div className="text-silver-light text-sm">{transaction.paymentDisplayMethod || transaction.paymentMethod}</div>
            </div>
            <div>
              <div className="text-silver text-xs">Time:</div>
              <div className="text-silver-light text-sm">{formatTime(transaction.timestamp)}</div>
            </div>
          </div>
          
          {transaction.description && (
            <div className="mt-3">
              <div className="text-silver text-xs">Description:</div>
              <div className="text-silver-light text-sm bg-navy-dark p-2 rounded mt-1">
                {transaction.description}
              </div>
            </div>
          )}
          
          {transaction.currency && (
            <div className="mt-2 text-xs text-silver">
              Currency: {transaction.displayCurrency || transaction.currency}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-navy-deep p-3 font-body">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-silver-light">User Fund History</h1>
        <p className="text-silver text-sm">All funds added by users (User Added & Bonus)</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-navy-dark border border-border rounded-lg p-3">
          <div className="text-silver text-xs">Total Transactions</div>
          <div className="text-silver-light text-xl font-bold">{stats.totalTransactions}</div>
          <div className="flex flex-wrap gap-2 mt-1 text-xs">
            <span className="text-blue-400">👤 {stats.userAddedCount}</span>
            {/* <span className="text-purple-400">🎁 {stats.bonusCount}</span> */}
            <span className="text-orange-400">👑 {stats.adminCount}</span>
          </div>
        </div>
        <div className="bg-navy-dark border border-border rounded-lg p-3">
          <div className="text-silver text-xs">Total Amount</div>
          <div className="text-blue-400 text-xl font-bold">${stats.totalAmount.toFixed(2)}</div>
          <div className="flex flex-wrap gap-2 mt-1 text-xs">
            <span className="text-blue-400">👤 ${stats.userAddedAmount.toFixed(2)}</span>
            {/* <span className="text-purple-400">🎁 ${stats.bonusAmount.toFixed(2)}</span> */}
            <span className="text-orange-400">👑 ${stats.adminAmount.toFixed(2)}</span>
          </div>
        </div>
        <div className="bg-navy-dark border border-border rounded-lg p-3">
          <div className="text-silver text-xs">Today's Transactions</div>
          <div className="text-silver-light text-xl font-bold">{stats.todayTransactions}</div>
        </div>
        <div className="bg-navy-dark border border-border rounded-lg p-3">
          <div className="text-silver text-xs">Today's Amount</div>
          <div className="text-blue-400 text-xl font-bold">${stats.todayAmount.toFixed(2)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-navy-dark border border-border rounded-lg p-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
          <div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ID, name, payment..."
              className="w-full px-3 py-2 !bg-navy-medium !text-silver-light border border-border rounded placeholder:text-silver/50 focus:outline-none focus:border-primary text-sm"
              style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}
            />
          </div>
          <div>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 !bg-navy-medium !text-silver-light border border-border rounded focus:outline-none focus:border-primary text-sm [color-scheme:dark]"
              style={{ backgroundColor: '#1f2937', color: '#e5e7eb', colorScheme: 'dark' }}
            />
          </div>
          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 !bg-navy-medium !text-silver-light border border-border rounded focus:outline-none focus:border-primary text-sm"
              style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}
            >
              <option value="">All Types</option>
              <option value="User Added">👤 User Added</option>
              {/* <option value="bonus">🎁 Welcome Bonus</option> */}
              <option value="admin">👑 Admin Added</option>
            </select>
          </div>
          <div>
              <input
                type="text"
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
                placeholder="Filter by payment method..."
                className="w-full px-3 py-2 !bg-navy-medium !text-silver-light border border-border rounded placeholder:text-silver/50 focus:outline-none focus:border-primary text-sm"
                style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}
              />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleClearFilters}
              className="flex-1 bg-navy-medium hover:bg-navy-medium/80 text-silver-light py-2 px-3 rounded text-sm border border-border"
            >
              Clear
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex-1 bg-primary hover:bg-primary/80 text-white py-2 px-3 rounded text-sm"
            >
              {loading ? '...' : 'Refresh'}
            </button>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-silver text-sm">
            Showing {filteredTransactions.length} transactions
          </div>
          <button
            onClick={exportToCSV}
            disabled={filteredTransactions.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-navy-dark border border-border rounded-lg p-3">
        <h2 className="text-silver-light text-lg font-bold mb-3">User Transactions</h2>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            <span className="ml-3 text-silver">Loading transactions...</span>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-silver text-sm">
              {searchTerm || dateFilter || typeFilter || paymentMethodFilter ? 'No transactions found' : 'No transactions yet'}
            </p>
          </div>
        ) : isMobile ? (
          // Mobile View - Accordion
          <div className="space-y-3">
            {filteredTransactions.map((transaction) => (
              <MobileTransactionCard key={transaction.id} transaction={transaction} />
            ))}
          </div>
        ) : (
          // Desktop View - Table
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 text-silver text-sm">S.No</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Date & Time</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Type</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Sponsor ID</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Member</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Amount</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Payment Method</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Network/Source</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Status</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Balance</th>
                  <th className="text-left py-2 px-2 text-silver text-sm">Payment ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-border/30 hover:bg-navy-medium">
                    <td className="py-2 px-2 text-silver text-sm">#{transaction.sno}</td>
                    <td className="py-2 px-2">
                      <div className="text-silver text-sm">{formatDate(transaction.timestamp)}</div>
                      <div className="text-silver/70 text-xs">{formatTime(transaction.timestamp)}</div>
                    </td>
                    <td className="py-2 px-2">{getTypeBadge(transaction)}</td>
                    <td className="py-2 px-2 text-cyan-accent text-sm font-medium">
                      {transaction.sponsorId}
                    </td>
                    <td className="py-2 px-2">
                      <div className="text-silver-light text-sm">{transaction.userName}</div>
                      <div className="text-silver text-xs">{transaction.memberId}</div>
                    </td>
                    <td className="py-2 px-2 text-blue-400 text-sm font-bold">
                      ${(transaction.amount || transaction.userAddedFund || 0).toFixed(2)}
                    </td>
                    <td className="py-2 px-2 text-silver text-sm">
                      {transaction.paymentDisplayMethod || transaction.paymentMethod || 'Crypto'}
                    </td>
                    <td className="py-2 px-2 text-silver text-sm">
                      {transaction.network || transaction.source || '-'}
                    </td>
                    <td className="py-2 px-2">{getStatusBadge(transaction.status)}</td>
                    <td className="py-2 px-2">
                      <div className="text-silver text-xs">Prev: ${transaction.previousFundBalance?.toFixed(2)}</div>
                      <div className="text-blue-400 text-xs">New: ${transaction.fundBalance?.toFixed(2)}</div>
                    </td>
                    <td className="py-2 px-2 text-silver text-xs font-mono truncate max-w-[100px]">
                      {transaction.paymentId || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserFundHistory;